<template>
  <div class="task-templates-manager">
    <div v-if="loading" class="muted" style="margin-bottom: 12px">{{ __('Loading…') }}</div>
    <div v-else-if="templates.length === 0" class="muted" style="margin-bottom: 12px">
      {{ emptyMessage }}
    </div>
    <ul v-else style="list-style: none; padding: 0; margin: 0 0 12px 0">
      <li v-for="template in templates" :key="template.id" class="template-item">
        <div style="flex: 1">
          <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 4px">
            <strong>{{ template.taskTitle || template.task_title }}</strong>
            <span v-if="template.description" class="muted" style="font-size: 13px">
              — {{ template.description }}
            </span>
          </div>
        </div>
        <div v-if="template.canManageTaskTemplate" style="display: flex; gap: 8px">
          <button class="btn-sm" :disabled="disabled" @click="editTemplate(template)">
            <span class="btn-text">{{ s__('TaskTemplates|Edit') }}</span>
            <span><EditIcon /></span>
          </button>
          <button class="btn-sm btn-danger" :disabled="disabled" @click="deleteTemplate(template)">
            <span class="btn-text">{{ s__('TaskTemplates|Delete') }}</span>
            <span><TrashIcon /></span>
          </button>
        </div>
      </li>
    </ul>
    <button
      v-if="showCreateButton && canCreateTemplate"
      :disabled="disabled"
      @click="openCreateModal"
    >
      {{ addButtonText }}
    </button>

    <!-- Create/Edit Template Modal -->
    <CreateTaskTemplateModal
      v-model="showCreateModal"
      :scope="scope"
      :project-id="projectId"
      :user-id="userId"
      :template="editingTemplate"
      :creating="creating"
      :updating="updating"
      :error="templateError"
      @submit="handleTemplateSubmit"
      @close="handleModalClose"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useLazyQuery, useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __, sprintf } from '@/locale'
import { showConfirm } from '@/utils/telegramWebApp'
import CreateTaskTemplateModal from './CreateTaskTemplateModal.vue'
import EditIcon from './icons/EditIcon.vue'
import TrashIcon from './icons/TrashIcon.vue'

const props = defineProps({
  scope: {
    type: String,
    required: true,
    validator: value => ['project', 'user'].includes(value),
  },
  projectId: {
    type: String,
    required: false,
    default: null,
  },
  userId: {
    type: String,
    required: false,
    default: null,
  },
  expanded: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  emptyMessage: {
    type: String,
    default: '',
  },
  addButtonText: {
    type: String,
    default: '',
  },
  canCreateTemplate: {
    type: Boolean,
    default: true,
  },
  showCreateButton: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['refetch'])

// State
const templates = ref([])
const showCreateModal = ref(false)
const editingTemplate = ref(null)
const creating = ref(false)
const updating = ref(false)
const templateError = ref('')

// GraphQL queries
const TASK_TEMPLATES_QUERY = gql`
  query TaskTemplates($userId: ID, $projectId: ID) {
    taskTemplates(userId: $userId, projectId: $projectId) {
      id
      description
      taskTitle
      taskDescription
      defaultAssigneeId
      defaultPriority
      defaultTaskCategoryId
      defaultWorkflowStatusId
      defaultParentTaskId
      defaultConfidential
      canManageTaskTemplate
      checklistData
      labels {
        id
        title
        color
      }
      personalLabels {
        id
        title
        color
      }
      attachments {
        id
        filename
        humanSize
      }
    }
  }
`

// GraphQL mutations
const CREATE_TASK_TEMPLATE = gql`
  mutation CreateTaskTemplate(
    $userId: ID
    $projectId: ID
    $description: String
    $taskTitle: String!
    $taskDescription: String
    $defaultAssigneeId: ID
    $defaultPriority: PriorityEnum
    $defaultTaskCategoryId: ID
    $defaultWorkflowStatusId: ID
    $defaultParentTaskId: ID
    $defaultLabels: [ID!]
    $defaultPersonalLabels: [ID!]
    $defaultConfidential: Boolean
    $checklistData: JSON
    $attachmentDraftId: ID
    $existingAttachmentIds: [ID!]
  ) {
    createTaskTemplate(
      userId: $userId
      projectId: $projectId
      description: $description
      taskTitle: $taskTitle
      taskDescription: $taskDescription
      defaultAssigneeId: $defaultAssigneeId
      defaultPriority: $defaultPriority
      defaultTaskCategoryId: $defaultTaskCategoryId
      defaultWorkflowStatusId: $defaultWorkflowStatusId
      defaultParentTaskId: $defaultParentTaskId
      defaultLabels: $defaultLabels
      defaultPersonalLabels: $defaultPersonalLabels
      defaultConfidential: $defaultConfidential
      checklistData: $checklistData
      attachmentDraftId: $attachmentDraftId
      existingAttachmentIds: $existingAttachmentIds
    ) {
      taskTemplate {
        id
        description
        taskTitle
        taskDescription
        defaultAssigneeId
        defaultPriority
        defaultTaskCategoryId
        defaultWorkflowStatusId
        defaultParentTaskId
        defaultConfidential
        canManageTaskTemplate
        checklistData
        labels {
          id
          title
          color
        }
        personalLabels {
          id
          title
          color
        }
        attachments {
          id
          filename
          humanSize
        }
      }
      errors
    }
  }
`

const UPDATE_TASK_TEMPLATE = gql`
  mutation UpdateTaskTemplate(
    $id: ID!
    $description: String
    $taskTitle: String
    $taskDescription: String
    $defaultAssigneeId: ID
    $defaultPriority: PriorityEnum
    $defaultTaskCategoryId: ID
    $defaultWorkflowStatusId: ID
    $defaultParentTaskId: ID
    $defaultLabels: [ID!]
    $defaultPersonalLabels: [ID!]
    $defaultConfidential: Boolean
    $checklistData: JSON
    $attachmentDraftId: ID
    $existingAttachmentIds: [ID!]
  ) {
    updateTaskTemplate(
      id: $id
      description: $description
      taskTitle: $taskTitle
      taskDescription: $taskDescription
      defaultAssigneeId: $defaultAssigneeId
      defaultPriority: $defaultPriority
      defaultTaskCategoryId: $defaultTaskCategoryId
      defaultWorkflowStatusId: $defaultWorkflowStatusId
      defaultParentTaskId: $defaultParentTaskId
      defaultLabels: $defaultLabels
      defaultPersonalLabels: $defaultPersonalLabels
      defaultConfidential: $defaultConfidential
      checklistData: $checklistData
      attachmentDraftId: $attachmentDraftId
      existingAttachmentIds: $existingAttachmentIds
    ) {
      taskTemplate {
        id
        description
        taskTitle
        taskDescription
        defaultAssigneeId
        defaultPriority
        defaultTaskCategoryId
        defaultWorkflowStatusId
        defaultParentTaskId
        defaultConfidential
        canManageTaskTemplate
        checklistData
        labels {
          id
          title
          color
        }
        personalLabels {
          id
          title
          color
        }
        attachments {
          id
          filename
          humanSize
        }
      }
      errors
    }
  }
`

const DELETE_TASK_TEMPLATE = gql`
  mutation DeleteTaskTemplate($id: ID!) {
    deleteTaskTemplate(id: $id) {
      taskTemplate {
        id
      }
      errors
    }
  }
`

// Get viewer's Telegram user ID for user scope
const viewerUid = computed(() => {
  const tg = window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initDataUnsafe
  return (tg && tg.user && tg.user.id) || null
})

// Lazy query setup
const {
  load: loadTemplates,
  result: templatesResult,
  loading: templatesLoading,
  refetch: refetchTemplates,
} = useLazyQuery(
  TASK_TEMPLATES_QUERY,
  () => {
    if (props.scope === 'project') {
      return { projectId: props.projectId }
    } else {
      // Use userId prop (database ID) - required for resolver
      // Note: The resolver expects database user ID, not Telegram ID
      return { userId: props.userId }
    }
  },
  () => ({
    enabled: props.expanded && (props.scope === 'project' ? !!props.projectId : !!props.userId),
  })
)

// Mutations
const { mutate: createTemplateMutation } = useMutation(CREATE_TASK_TEMPLATE, {
  errorPolicy: 'all',
})
const { mutate: updateTemplateMutation } = useMutation(UPDATE_TASK_TEMPLATE)
const { mutate: deleteTemplateMutation } = useMutation(DELETE_TASK_TEMPLATE, {
  errorPolicy: 'all',
})

// Computed
const loading = computed(() => templatesLoading.value)

// Watch for expanded prop changes to trigger loading
watch(
  () => props.expanded,
  expanded => {
    if (expanded && !templatesResult.value && !templatesLoading.value) {
      if (props.scope === 'project' && props.projectId) {
        loadTemplates()
      } else if (props.scope === 'user' && props.userId) {
        loadTemplates()
      }
    }
  },
  { immediate: true }
)

// Watch for query results
watch(
  templatesResult,
  data => {
    if (data?.taskTemplates) {
      templates.value = data.taskTemplates || []
    }
  },
  { immediate: true }
)

function editTemplate(template) {
  editingTemplate.value = template
  showCreateModal.value = true
}

function handleModalClose() {
  showCreateModal.value = false
  editingTemplate.value = null
  templateError.value = ''
}

function openCreateModal() {
  if (props.disabled || !props.canCreateTemplate) return
  editingTemplate.value = null
  templateError.value = ''
  showCreateModal.value = true
}

async function handleTemplateSubmit(formData) {
  templateError.value = ''

  if (editingTemplate.value) {
    // Update existing template
    updating.value = true
    try {
      const { data, errors: graphqlErrors } = await updateTemplateMutation({
        id: editingTemplate.value.id,
        ...formData,
      })

      if (data?.updateTaskTemplate?.errors?.length) {
        const errorMessages = data.updateTaskTemplate.errors
          .map(e => (typeof e === 'string' ? e : e.message || String(e)))
          .join(', ')
        templateError.value = errorMessages
        updating.value = false
        return
      }

      if (graphqlErrors && graphqlErrors.length > 0) {
        templateError.value = graphqlErrors[0]?.message || 'Failed to update template'
        updating.value = false
        return
      }

      handleModalClose()

      // Refetch templates
      if (props.expanded && refetchTemplates) {
        await refetchTemplates()
      } else if (props.expanded) {
        await loadTemplates()
      }

      emit('refetch')
    } catch (e) {
      templateError.value = e.message || 'Failed to update template'
    } finally {
      updating.value = false
    }
  } else {
    // Create new template
    creating.value = true
    try {
      const mutationVars = {
        ...formData,
      }

      if (props.scope === 'project') {
        mutationVars.projectId = props.projectId
      } else {
        // Use userId prop if provided, otherwise fall back to viewerUid (Telegram ID)
        // Note: The mutation expects database user ID, not Telegram ID
        mutationVars.userId = props.userId || (viewerUid.value ? String(viewerUid.value) : null)
      }

      const { data, errors: graphqlErrors } = await createTemplateMutation(mutationVars)

      if (data?.createTaskTemplate?.errors?.length) {
        const errorMessages = data.createTaskTemplate.errors
          .map(e => (typeof e === 'string' ? e : e.message || String(e)))
          .join(', ')
        templateError.value = errorMessages
        creating.value = false
        return
      }

      if (graphqlErrors && graphqlErrors.length > 0) {
        templateError.value = graphqlErrors[0]?.message || 'Failed to create template'
        creating.value = false
        return
      }

      handleModalClose()

      // Refetch templates
      if (props.expanded && refetchTemplates) {
        await refetchTemplates()
      } else if (props.expanded) {
        await loadTemplates()
      }

      emit('refetch')
    } catch (e) {
      templateError.value = e.message || 'Failed to create template'
    } finally {
      creating.value = false
    }
  }
}

async function deleteTemplate(template) {
  const confirmMessage = sprintf(s__('TaskTemplates|Delete template "%{name}"?'), {
    name: template.taskTitle || template.task_title,
  })
  const confirmed = await showConfirm(confirmMessage)
  if (!confirmed) return

  try {
    const result = await deleteTemplateMutation({ id: template.id })

    // Handle both possible result structures:
    // 1. { data: {...}, errors: [...] } - standard Apollo structure
    // 2. Direct data object if errorPolicy handles it differently
    const data = result?.data || result
    const graphqlErrors = result?.errors || []

    // Check for GraphQL errors first
    if (graphqlErrors && graphqlErrors.length > 0) {
      const errorMessages = graphqlErrors
        .map(e => {
          if (typeof e === 'string') return e
          if (e?.message) return e.message
          if (e?.error) return String(e.error)
          if (e?.toString && typeof e.toString === 'function') return e.toString()
          return JSON.stringify(e, null, 2)
        })
        .join(', ')
      alert('Failed to delete template: ' + errorMessages)
      return
    }

    // Check for mutation response errors
    if (data?.deleteTaskTemplate?.errors?.length) {
      const errorMessages = data.deleteTaskTemplate.errors
        .map(e => {
          if (typeof e === 'string') return e
          if (e?.message) return e.message
          if (e?.error) return String(e.error)
          if (e?.toString && typeof e.toString === 'function') return e.toString()
          return JSON.stringify(e, null, 2)
        })
        .join(', ')
      alert('Failed to delete template: ' + errorMessages)
      return
    }

    // Refetch templates
    if (props.expanded && refetchTemplates) {
      await refetchTemplates()
    } else if (props.expanded) {
      await loadTemplates()
    }

    emit('refetch')
  } catch (e) {
    let errorMessage = 'Unknown error'
    if (typeof e === 'string') {
      errorMessage = e
    } else if (e?.message) {
      errorMessage = e.message
    } else if (e?.error) {
      errorMessage = String(e.error)
    } else if (e?.toString && typeof e.toString === 'function') {
      errorMessage = e.toString()
    } else {
      errorMessage = JSON.stringify(e, null, 2)
    }
    alert('Failed to delete template: ' + errorMessage)
  }
}

// Expose methods for parent components
defineExpose({
  loadTemplates,
  refetch: refetchTemplates || loadTemplates,
  openCreateModal,
})
</script>

<style scoped>
.task-templates-manager {
  width: 100%;
}

.template-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: white;
  border-radius: 4px;
  margin-bottom: 8px;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #5a6268;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #c82333;
}

/* Button styles inherit from forms.css */

.muted {
  color: #999;
  font-size: 14px;
}
</style>
